import { AloftPrice, Transaction, isDefined, median } from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectCompSequence } from './orderSelectors.ts';
import { selectTransactionEntities } from './propertyDataSelectors/transactionSelectors.ts';
import { getShouldApplyDateOfSale } from './utils/getShouldApplyDateOfSale.ts';

const getSalesChartData = (transaction: Transaction) => {
  const DOM = transaction.daysOnMarket;

  const originalListPrice = isDefined(transaction.originalListPrice)
    ? AloftPrice.schema.parse(transaction.originalListPrice)
    : undefined;
  const closePrice = isDefined(transaction.price)
    ? AloftPrice.schema.parse(transaction.price)
    : undefined;
  const salePriceRatio =
    isDefined(closePrice) && isDefined(originalListPrice)
      ? closePrice.ratio(originalListPrice)
      : undefined;

  return { originalListPrice, closePrice, DOM, salePriceRatio };
};

const shouldApplyDateOfSaleForTransaction = (transaction: Transaction): boolean => {
  const listingStatus = transaction.status;

  if (!isDefined(listingStatus)) return false;
  return getShouldApplyDateOfSale(listingStatus);
};

const selectSalesChartTransactions = createSelector([selectTransactionEntities], (transactions) =>
  Object.fromEntries(
    Object.entries(transactions).filter(([_, transaction]) =>
      shouldApplyDateOfSaleForTransaction(transaction),
    ),
  ),
);

const selectMedianSalesChartData = createSelector(
  [selectSalesChartTransactions],
  (transactions) => {
    const salesChartData = Object.values(transactions).map(getSalesChartData);

    try {
      const DOM = median(salesChartData.map((data) => data.DOM).filter(isDefined));
      const originalListPriceMedian = median(
        salesChartData.map((data) => data.originalListPrice?.toDollarLike()).filter(isDefined),
      );
      const closePriceMedian = median(
        salesChartData.map((data) => data.closePrice?.toDollarLike()).filter(isDefined),
      );
      const salePriceRatioMedian = median(
        salesChartData.map((data) => data.salePriceRatio).filter(isDefined),
      );
      const originalListPrice =
        isDefined(originalListPriceMedian) && !Number.isNaN(originalListPriceMedian)
          ? AloftPrice.fromDollarLike(originalListPriceMedian)
          : undefined;
      const closePrice =
        isDefined(closePriceMedian) && !Number.isNaN(closePriceMedian)
          ? AloftPrice.fromDollarLike(closePriceMedian)
          : undefined;

      const salePriceAsPercentageOfListPrice = isDefined(salePriceRatioMedian)
        ? Math.round(salePriceRatioMedian * 100)
        : undefined;

      return {
        header: 'Median',
        DOM: Number.isNaN(DOM) ? '' : DOM?.toString(),
        originalListPrice: originalListPrice?.toString(true),
        closePrice: closePrice?.toString(true),
        salePriceAsPercentageOfListPrice: isDefined(salePriceAsPercentageOfListPrice)
          ? `${salePriceAsPercentageOfListPrice}%`
          : undefined,
      };
    } catch (e) {
      console.warn(e);
      return {
        header: 'Median',
        DOM: '',
        originalListPrice: '',
        closePrice: '',
        salePriceAsPercentageOfListPrice: '',
      };
    }
  },
);

export const selectComparableSalesChartData = createSelector(
  [selectCompSequence, selectSalesChartTransactions, selectMedianSalesChartData],
  (compSequence, transactions, medianChartData) =>
    compSequence
      .map((compId, idx) => {
        const transaction = transactions[compId];
        if (!transaction) {
          return undefined;
        }

        const { DOM, originalListPrice, closePrice, salePriceRatio } =
          getSalesChartData(transaction);

        const salePriceAsPercentageOfListPrice = isDefined(salePriceRatio)
          ? Math.round(salePriceRatio * 100)
          : undefined;

        return {
          header: `Comp ${idx + 1}`,
          DOM: DOM?.toString(),
          originalListPrice: originalListPrice?.toString(true),
          closePrice: closePrice?.toString(true),
          salePriceAsPercentageOfListPrice: isDefined(salePriceAsPercentageOfListPrice)
            ? `${salePriceAsPercentageOfListPrice}%`
            : undefined,
        };
      })
      .filter(isDefined)
      .concat([medianChartData]),
);
